import React, { memo, FC, useCallback, useMemo } from 'react';
import _ from 'underscore';
import {
  Caption1,
  Caption1Stronger,
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  Image,
  useTableSort,
  useTableFeatures,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import classNames from 'classnames';
import { OverviewCardFooter, usePaginationConfig, IFooterButtonConfig } from '@bingads-webui-campaign-react/overview-card-footer';

import { IPerformanceData, ISelectedMetric, ISortState, IColumnData, IMetricFormatter, TMajorMetric } from './types';
import { getColumns } from './columns';
import { TableShimmer } from './table-shimmer';
import { EntityNameColumnId } from './const';
import SearchExclamationImage from '../media/search-exclamation-image.png';

export const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  sorted: {
    backgroundColor: tokens.colorSubtleBackgroundHover,
    flexGrow: 1.5,
  },
  entityNameColumn: {
    flexGrow: 1.8,
  },
  noDataContainer: {
    flexGrow: 1,
    textAlign: 'center',
    alignContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export interface IOverviewPerformanceTableProps {
  isLoading?: boolean;
  entityDisPlayName: string;
  selectedMetrics?: ISelectedMetric[];
  allMetrics: ISelectedMetric[];
  sortState?: ISortState;
  data?: IPerformanceData[];
  onChange: ({
    newSelectedMetrics,
    newSortState,
  }: {
    newSelectedMetrics?: ISelectedMetric[],
    newSortState?: ISortState,
  }) => void;
  i18n: any;
  metricFormatter: IMetricFormatter;
  comparisonMajorMetric: TMajorMetric;
  footerButtonConfigs?: IFooterButtonConfig[];
  getHref: Function;
  getPopoverContent?: Function;
  triggerSearchTermDrawer?: Function;
  entityType: string;
}

export const OverviewPerformanceTable: FC<IOverviewPerformanceTableProps> = memo((props: IOverviewPerformanceTableProps) => {
  const {
    isLoading,
    entityDisPlayName,
    selectedMetrics,
    allMetrics,
    sortState,
    data,
    onChange,
    i18n,
    metricFormatter,
    comparisonMajorMetric,
    footerButtonConfigs,
    getHref,
    getPopoverContent,
    triggerSearchTermDrawer,
    entityType,
  } = props;
  const classes = useStyles();

  const onColumnChange = useCallback(({ newSelectedMetrics }: {
    newSelectedMetrics: ISelectedMetric[],
  }) => {
    if (!newSelectedMetrics.some(m => m.key === sortState.sortColumn)) {
      const changedMetric = newSelectedMetrics.find(m => selectedMetrics.every(sm => sm.key !== m.key));
      const newSortState = {
        sortColumn: changedMetric.key,
        sortDirection: sortState.sortDirection,
      };
      onChange({
        newSelectedMetrics,
        newSortState,
      });
      return;
    }
    onChange({
      newSelectedMetrics,
    });
  }, [onChange, selectedMetrics, sortState]);

  const columns = getColumns({
    entityDisPlayName,
    selectedMetrics,
    allMetrics,
    onColumnChange,
    metricFormatter,
    comparisonMajorMetric,
    i18n,
  });

  const {
    getDataToDisplayFromSortedData,
    renderPagination,
    resetToFirstPage,
  } = usePaginationConfig<IPerformanceData>({
    data,
    i18n,
  });

  const onSortChange = useCallback((e, nextSortState) => {
    if (sortState.sortColumn === nextSortState.sortColumn) {
      onChange({
        newSortState: nextSortState,
      });
    } else {
      // For performance metrics, we reset sort direction to descending when sort column changes
      onChange({
        newSortState: {
          sortColumn: nextSortState.sortColumn,
          sortDirection: 'descending',
        },
      });
    }
    resetToFirstPage();
  }, [onChange, resetToFirstPage, sortState]);

  const {
    getRows,
    sort: { sort },
  } = useTableFeatures<IPerformanceData>(
    {
      columns,
      items: data,
    },
    [
      useTableSort({
        sortState,
      }),
    ]
  );

  const dataToDisplay = useMemo(() => {
    const sortedData = _.map(sort(getRows()), row => row.item);
    return getDataToDisplayFromSortedData(sortedData);
  }, [getDataToDisplayFromSortedData, getRows, sort]);
  const isSortColumn = useCallback((columnId: string | number) => columnId === sortState.sortColumn, [sortState]);
  const sortClassObj = useCallback((columnId: string | number) => ({
    [classes.sorted]: isSortColumn(columnId),
  }), [classes.sorted, isSortColumn]);

  if (isLoading) {
    return <TableShimmer />;
  }

  return (
    <div className={classNames(classes.container)}>
      <DataGrid
        items={dataToDisplay}
        columns={columns}
        sortable
        sortState={sortState}
        onSortChange={onSortChange}
        getRowId={item => item.Id}
        className={classNames('ba-performance-table', classes.grid)}
      >
        <DataGridHeader>
          <DataGridRow className={classNames('ba-performance-table-header-row')}>
            {({ renderHeaderCell, columnId }) => (
              <DataGridHeaderCell
                className={classNames(
                  `ba-performance-table-column-header-${columnId}`,
                  sortClassObj(columnId),
                  { [classes.entityNameColumn]: columnId === EntityNameColumnId }
                )}
              >
                {renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<IColumnData>>
          {({ item, rowId }) => (
            <DataGridRow<IColumnData> key={rowId}>
              {({ renderCell, columnId }) => {
                const columnData = {
                  ...item,
                  href: getHref(`${item.Id}`, item.CampaignType),
                  columnConfig: {
                    isSorted: isSortColumn(columnId),
                    columnId,
                  },
                  getPopoverContent,
                  triggerSearchTermDrawer,
                  entityType,
                  currency: item.Currency,
                };
                return (
                  <DataGridCell className={classNames(`ba-performance-table-column-${columnId}`, sortClassObj(columnId), { [classes.entityNameColumn]: columnId === EntityNameColumnId })}>
                    {renderCell(columnData)}
                  </DataGridCell>
                );
              }}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
      {_.isEmpty(dataToDisplay) &&
        <div className={classNames('no-data-container', classes.noDataContainer)}>
          <div className={classNames('ba-overview-no-data-image')}>
            <Image src={SearchExclamationImage} height={50} width={50} /><br />
          </div>
          <Caption1Stronger style={{ display: 'block', marginTop: '1px' }}>{i18n.getString(_TL_('No data available. '))}</Caption1Stronger><br />
          <div className={classNames('ba-overview-no-data-available-text')}>
            <Caption1 style={{ display: 'block', marginTop: '1px' }}>
              {i18n.getString(_TL_('To update the tile, select a different date range.'))}
            </Caption1>
            <Caption1 style={{ display: 'block', marginTop: '1px' }}>
              {i18n.getString(_TL_('If the tile remains blank, your account might have no data.'))}
            </Caption1>
          </div>
        </div>
      }
      <OverviewCardFooter footerButtonConfigs={footerButtonConfigs} renderPagination={renderPagination} />
    </div>
  );
});

OverviewPerformanceTable.defaultProps = {
  isLoading: false,
  selectedMetrics: undefined,
  sortState: undefined,
  data: [],
  footerButtonConfigs: null,
  getPopoverContent: undefined,
  triggerSearchTermDrawer: undefined,
};
